import type { EligibilityRule, OnboardingProduct } from './admin-api';

/**
 * Manage Account config parsing (SCRUM-433). The tenant's onboarding_config JSON
 * is stored verbatim by the wizard (OnboardingWizard's OnboardingConfig shape) and
 * returned raw by GET /api/admin/tenants/{id}. These helpers parse the three
 * config-backed sections the Manage Account page renders and edits — intelligence
 * triggers and products — into typed, honest-empty structures,
 * and serialize edited sections back for the PATCH /config per-key merge.
 *
 * The wizard and this page share the v7 trigger catalog (keys + field labels) so a
 * config captured at onboarding round-trips through Manage Account unchanged.
 */

/**
 * A bare DNS domain: labels of alnum/hyphen, at least one dot, no scheme/@/space.
 * Shared by the Email Domains editor and the wizard's SSO & Access step
 * (SCRUM-594) — mirrors the server's Tenant.ConfigureEmailDomains shape rule.
 */
export const DOMAIN_RE = /^(?!-)[a-z0-9-]+(?<!-)(\.(?!-)[a-z0-9-]+(?<!-))+$/;

/** One editable threshold field within a trigger (matches the wizard's TRIGGER_DEFAULTS). */
export interface TriggerFieldMeta {
  key: string;
  label: string;
}

/** A trigger's display + edit metadata; values live in the config's intelligence map. */
export interface TriggerMeta {
  key: string;
  name: string;
  fields: TriggerFieldMeta[];
}

/**
 * v7 intelligence-trigger catalog — the SAME keys and field labels the onboarding
 * wizard captures (OnboardingWizard TRIGGER_DEFAULTS). The Manage Account read/edit
 * views iterate this catalog and pull each value from config.intelligence[key][field].
 */
export const TRIGGER_CATALOG: TriggerMeta[] = [
  {
    key: 'dormantAccount',
    name: 'Dormant Account',
    fields: [
      { key: 'inactivityDays', label: 'Inactivity Threshold (days)' },
      { key: 'excludeUnder', label: 'Exclude Transactions Under' },
    ],
  },
  {
    key: 'depositFlightRisk',
    name: 'Deposit Flight Risk',
    fields: [
      { key: 'declinePct', label: 'Decline Threshold (%)' },
      { key: 'lookbackDays', label: 'Lookback Window (days)' },
    ],
  },
  {
    key: 'smbGrowth',
    name: 'SMB Growth',
    fields: [
      { key: 'growthPct', label: 'Growth Threshold (%)' },
      { key: 'minBalance', label: 'Min Account Balance' },
    ],
  },
  {
    key: 'relationshipGap',
    name: 'Relationship Gap',
    fields: [
      { key: 'checkingThreshold', label: 'Checking Balance Threshold' },
      { key: 'maxSavingsRatio', label: 'Max Savings-to-Checking Ratio' },
    ],
  },
];

/** The intelligence map: trigger key → field key → string value. */
export type TriggerConfig = Record<string, Record<string, string>>;

/** The three config-backed sections the Manage Account page renders + edits. */
export interface ParsedConfig {
  intelligence: TriggerConfig;
  products: OnboardingProduct[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Coerce a stored trigger value (string | number | undefined) to the editor's string. */
function triggerValue(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  return '';
}

/** Parse the raw intelligence map into a fully-populated string map over the catalog. */
function parseIntelligence(raw: unknown): TriggerConfig {
  const source = isRecord(raw) ? raw : {};
  const out: TriggerConfig = {};
  for (const trigger of TRIGGER_CATALOG) {
    const triggerRaw = isRecord(source[trigger.key]) ? source[trigger.key] : {};
    const fields: Record<string, string> = {};
    for (const field of trigger.fields) {
      fields[field.key] = triggerValue((triggerRaw as Record<string, unknown>)[field.key]);
    }
    out[trigger.key] = fields;
  }
  return out;
}

function parseRules(raw: unknown): EligibilityRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r): EligibilityRule[] => {
    if (!isRecord(r)) return [];
    return [
      {
        field: typeof r.field === 'string' ? r.field : '',
        operator: typeof r.operator === 'string' ? r.operator : '',
        value:
          typeof r.value === 'string'
            ? r.value
            : typeof r.value === 'number'
              ? String(r.value)
              : '',
        required: r.required === true,
      },
    ];
  });
}

function parseProducts(raw: unknown): OnboardingProduct[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((p): OnboardingProduct[] => {
    if (!isRecord(p)) return [];
    return [
      {
        name: typeof p.name === 'string' ? p.name : '',
        category: typeof p.category === 'string' ? p.category : '',
        description: typeof p.description === 'string' ? p.description : '',
        rules: parseRules(p.rules),
        requiredMatches: typeof p.requiredMatches === 'number' ? p.requiredMatches : 1,
      },
    ];
  });
}

/**
 * Parse the raw onboarding_config JSON string into the three config-backed sections.
 * Null/blank/invalid JSON yields honest empty sections (no throw) — a tenant onboarded
 * without a wizard config, or a metadata-only showcase tenant, renders empty states.
 */
export function parseConfig(json: string | null): ParsedConfig {
  let doc: unknown = {};
  if (json) {
    try {
      doc = JSON.parse(json);
    } catch {
      doc = {};
    }
  }
  const source = isRecord(doc) ? doc : {};
  return {
    intelligence: parseIntelligence(source.intelligence),
    products: parseProducts(source.products),
  };
}

/** Serialize an intelligence map for the PATCH intelligenceJson field. */
export function serializeIntelligence(intelligence: TriggerConfig): string {
  return JSON.stringify(intelligence);
}

/** Serialize products for the PATCH productsJson field. */
export function serializeProducts(products: OnboardingProduct[]): string {
  return JSON.stringify(products);
}

/** v7 trigger read-view value formatting: "$10,000" for currency, "0.10" / "90" otherwise. */
export function formatTriggerValue(fieldKey: string, value: string): string {
  const isCurrency = fieldKey === 'excludeUnder' || fieldKey === 'minBalance';
  if (!isCurrency) return value || '—';
  // Values are captured with their own "$"/","/decimals in the wizard (e.g. "$10,000").
  // If a bare number slipped in, prefix a "$" for display parity; otherwise pass through.
  if (value === '') return '—';
  return value.startsWith('$') ? value : `$${value}`;
}

/** "1.8 MB" / "340 KB" for a byte count (matches the Vault UploadModal formatting). */
export function formatBytes(bytes: number): string {
  if (bytes > 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes > 0) return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
  return '0 KB';
}

/** v7 vault "Uploaded" date: "Apr 22, 2026" (UTC, matching the batch-date convention). */
export function formatUploadedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
